/**
 * Partnership Detail Page - Clara Vale, human-readable.
 *
 * Shows partner info, user access management (email-based, not user ID),
 * and subtle suspend/reactivate controls.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ArrowLeft, ArrowUpRight, Building2, ChevronDown, ChevronRight, HardHat, Loader2, Mail, MapPin, Plus, Trash2, Users } from 'lucide-react'
import api from '@/admin/lib/api'
import { useOrg } from '@/admin/hooks/use-org'
import { usePartnerDuplicates } from '@/admin/hooks/use-partner-merge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { AppShell } from '@/admin/components/app-shell'
import { NotesPanel } from '@/admin/components/shared/notes-panel'
import { PartnershipProductAccess } from '@/admin/components/shared/partnership-product-access'
import { PartnershipWarrantyRegistrations } from '@/admin/components/shared/partnership-warranty-registrations'
import { PartnershipAttributeForm } from '@/admin/components/shared/partnership-attribute-form'
import { RegroupPartnerDialog } from '@/admin/pages/partnerships/regroup-partner-dialog'
import { MoveAccessDialog } from '@/admin/pages/partnerships/move-access-dialog'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { EditableDetailGridCard } from '@/admin/components/shared/editable-detail-grid-card'
import type { CardFieldItem, EditConfig } from '@/admin/components/shared/editable-card-types'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { useForm, defineField } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import type {
  Partnership,
  PartnershipUserAccess,
  PendingInvite,
} from '@/admin/types/organisations'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'

import { toastApiError } from '@/shared/lib/api-error'
const STRATEGY_LABELS: Record<string, string> = {
  managed: 'We manage their users',
  delegated: 'They manage their own users',
  direct_inheritance: 'All users get access automatically',
}

// EAV codes owned by the tenant (e.g. OC) rather than the partner -
// the point of contact assigned to the HQ. Child locations inherit
// their ASM from the parent, so we hide these fields from branch
// detail views and pull them into a dedicated card on the HQ view.
const ASM_ATTRIBUTE_CODES = ['asm_name', 'asm_email'] as const

// EAV codes whose values are synced from the partner network (LE365) as
// structured data rather than typed by a coordinator. They render in the
// partner profile panel (as a certifications table) and are kept out of
// the editable profile form, where a JSON list has no usable input.
const SYNC_OWNED_ATTRIBUTE_CODES = ['certifications'] as const

const PROFILE_FORM_EXCLUDED_CODES = [
  ...ASM_ATTRIBUTE_CODES,
  ...SYNC_OWNED_ATTRIBUTE_CODES,
] as const

/**
 * One row in the Manage menu: what it does, and what it costs.
 *
 * The description is the point. These actions are rare and consequential, and
 * a button label cannot say "the others are retired" or "they lose access
 * there" - so the learning curve moves here, where there is room for it.
 */
function MenuAction({
  title,
  description,
  onSelect,
  testId,
  tone = 'default',
  disabled = false,
}: {
  title: string
  description: string
  onSelect: () => void
  testId: string
  tone?: 'default' | 'warn' | 'go'
  disabled?: boolean
}) {
  const titleTone =
    tone === 'warn'
      ? 'text-[var(--status-warn)]'
      : tone === 'go'
        ? 'text-[var(--green)]'
        : 'text-[var(--ink)]'
  return (
    <Button
      variant="ghost"
      data-testid={testId}
      disabled={disabled}
      onClick={onSelect}
      className="h-auto w-full flex-col items-start gap-0.5 rounded-[6px] px-3 py-2.5 text-left font-normal hover:bg-[var(--bg-subtle)] disabled:opacity-50"
    >
      <span className={`text-[13px] ${titleTone}`}>{title}</span>
      <span className="text-[12px] leading-[1.5] text-[var(--ink-3)] whitespace-normal">
        {description}
      </span>
    </Button>
  )
}

export function PartnershipDetailPage() {
  const { uuid } = useParams<{ uuid: string }>()
  const { t } = useTranslation()
  const { orgUuid, isPartnerAccess } = useOrg()
  const { data: duplicateGroup } = usePartnerDuplicates(uuid)
  const [manageOpen, setManageOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const [partnership, setPartnership] = useState<Partnership | null>(null)
  const [userAccess, setUserAccess] = useState<PartnershipUserAccess[]>([])
  const [resendingInvite, setResendingInvite] = useState<string | null>(null)
  const [cancellingInvite, setCancellingInvite] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [togglingStatus, setTogglingStatus] = useState(false)
  const [regroupOpen, setRegroupOpen] = useState(false)
  const [accessOpen, setAccessOpen] = useState(false)
  const { can } = usePermissions()
  const canEditPartnerships = can('partnerships.edit')
  // Reassign or clear the downstream org's registered contact. Stale whenever
  // the named person moves to another site, and previously uncorrectable.
  const [editingContact, setEditingContact] = useState(false)
  const [savingContact, setSavingContact] = useState(false)
  const [savingContractor, setSavingContractor] = useState(false)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [revokingUuid, setRevokingUuid] = useState<string | null>(null)
  const [grantingUuid, setGrantingUuid] = useState<string | null>(null)
  const [confirmRevoke, setConfirmRevoke] =
    useState<PartnershipUserAccess | null>(null)

  // Invite dialog: `inviting` drives the submit spinner; the form itself
  // is a generated useForm (defined after the fetch callbacks below).
  const [inviting, setInviting] = useState(false)
  // Per-row spinner for the Invite buttons in the people list. The dialog's
  // `inviting` cannot serve both: one boolean spins every row at once, which
  // reads as "all of them are sending" when only one is. Keyed by address
  // because a not-yet-invited contact has no uuid to key on.
  const [invitingEmail, setInvitingEmail] = useState<string | null>(null)

  // Honour an explicit origin when one page links into another (People -> a
  // partner), so Back returns to the record you were reading rather than
  // dumping you in the partner list you never visited.
  const linkedFrom = (location.state ?? null) as
    | { backTo?: string; backLabel?: string }
    | null
  const backPath =
    linkedFrom?.backTo ??
    (location.pathname.startsWith('/network')
      ? '/network/partners'
      : '/network/partnerships')

  const fetchPartnership = useCallback(async () => {
    if (!orgUuid || !uuid) { setLoading(false); return }
    try {
      const res = await api.get(`/organisations/${orgUuid}/partnerships/${uuid}/`)
      setPartnership(res.data)
    } catch (err) {
      toastApiError(
        err,
        t('partnerships.failedToLoadDetails', 'Failed to load partner details.'),
      );
    } finally {
      setLoading(false)
    }
  }, [orgUuid, uuid, t])

  /**
   * Reassign or clear the downstream org's registered contact.
   *
   * Blank values CLEAR rather than store "" - a dealer whose named contact has
   * moved on is honestly contactless, and a placeholder person would put a
   * fabricated name on a real partner. Nothing sends to this field (that is
   * OrganisationEmailConfig.contact_email), so empty breaks no correspondence.
   */
  const saveContact = useCallback(
    async (values: { first_name: string; last_name: string; email: string }) => {
      if (!orgUuid || !uuid) return
      setSavingContact(true)
      try {
        await api.put(`/organisations/${orgUuid}/partnerships/${uuid}/`, {
          contact_first_name: values.first_name,
          contact_last_name: values.last_name,
          contact_email: values.email,
        })
        await fetchPartnership()
        setEditingContact(false)
        toast.success(
          values.email
            ? t('partnerships.contactUpdated', 'Contact updated.')
            : t('partnerships.contactCleared', 'Contact cleared.'),
        )
      } catch (err) {
        toastApiError(
          err,
          t('partnerships.failedToUpdateContact', 'Failed to update the contact.'),
        )
      } finally {
        setSavingContact(false)
      }
    },
    [orgUuid, uuid, fetchPartnership, t],
  )

  const fetchUserAccess = useCallback(async () => {
    if (!orgUuid || !uuid) return
    try {
      const res = await api.get(`/organisations/${orgUuid}/partnerships/${uuid}/user-access/`)
      setUserAccess(res.data.results ?? res.data)
    } catch { /* silent */ }
  }, [orgUuid, uuid])

  // Outstanding invitations for the partner's own organisation. Without these
  // an invited colleague is invisible until they accept, so nobody can tell an
  // ignored invite from one that was never sent. Its own registered key prefix
  // (LIST_LIVE) so a just-sent invite is never missing behind a cached snapshot.
  const downstreamUuid = partnership?.downstream_organisation.uuid

  /**
   * Staff of the partner org who hold NO access. This list was built purely
   * from PartnershipUserAccess rows, so someone on the partner's staff list who
   * was never granted access did not appear AT ALL - and they are exactly the
   * person an admin is hunting when a dealer says "my colleague cannot log in".
   * Network > People surfaced them; the partner page could not, so the two
   * surfaces disagreed about the same dealer.
   */
  const { data: partnerMembers = [] } = useQuery<
    { uuid: string; user_uuid: string; user_email: string; user_name: string }[]
  >({
    queryKey: ['partnership-members', downstreamUuid],
    queryFn: async () => {
      const res = await api.get(`/organisations/${downstreamUuid}/members/`, {
        params: { page_size: 200 },
      })
      return res.data.results ?? res.data ?? []
    },
    enabled: Boolean(downstreamUuid),
  })
  const { data: pendingInvites = [], refetch: refetchPendingInvites } = useQuery<
    PendingInvite[]
  >({
    queryKey: ['partnership-invitations', downstreamUuid],
    queryFn: async () => {
      const res = await api.get(`/organisations/${downstreamUuid}/invitations/`)
      const rows: PendingInvite[] = res.data.results ?? res.data ?? []
      return rows.filter((i) => i.status === 'pending')
    },
    enabled: Boolean(downstreamUuid),
  })


  // Show the migrated dealer's registered contact until THAT PERSON can sign in
  // - not until somebody can. Keyed on the email, because a colleague accepting
  // an invitation says nothing about whether the dealer's own contact has been
  // onboarded, and they are the one the business expects to reach. Dropped once
  // the contact holds access or has an invitation outstanding, so they never
  // appear twice.
  const contactEmail = (partnership?.contact_email ?? '').trim().toLowerCase()
  const showContactRow =
    Boolean(contactEmail) &&
    !userAccess.some((ua) => (ua.user_email ?? '').toLowerCase() === contactEmail) &&
    !pendingInvites.some((i) => (i.email ?? '').toLowerCase() === contactEmail)

  // The section renders ONE list. A person is in exactly one of three states,
  // and the list is ordered by them - still to invite, invited and waiting,
  // then signed in - so the rows an admin can act on are always at the top and
  // the settled ones sink. Rank, not three lists, because a merged dealership
  // carries several people in different states at once.
  type PersonRow = {
    key: string
    name: string
    email: string
    firstName: string
    lastName: string
  } & (
    | { kind: 'contact' }
    | { kind: 'invite'; invite: PendingInvite }
    | { kind: 'access'; access: (typeof userAccess)[number] }
    | { kind: 'member'; userUuid: string }
  )

  const people = useMemo<PersonRow[]>(() => {
    const rows: PersonRow[] = []

    // ONE place decides the display name. Each push used to do its own
    // fallback chain and the invite branch could still land on undefined
    // (no first/last AND no email), which crashed the sort below on
    // `a.name.localeCompare`. A cached or partial row is enough to trigger it,
    // so the guarantee belongs here rather than in three separate expressions.
    const displayName = (...candidates: (string | null | undefined)[]) =>
      candidates.map((c) => (c ?? '').trim()).find(Boolean) ??
      t('common.unnamedUser', 'Unnamed user')

    if (showContactRow && partnership?.contact_email) {
      const first = partnership.contact_first_name ?? ''
      const last = partnership.contact_last_name ?? ''
      rows.push({
        kind: 'contact',
        key: `contact:${partnership.contact_email}`,
        name: displayName([first, last].filter(Boolean).join(' '), partnership.contact_email),
        email: partnership.contact_email,
        firstName: first,
        lastName: last,
      })
    }
    for (const invite of pendingInvites) {
      const first = invite.first_name ?? ''
      const last = invite.last_name ?? ''
      rows.push({
        kind: 'invite',
        invite,
        key: `invite:${invite.uuid}`,
        name: displayName([first, last].filter(Boolean).join(' '), invite.email),
        email: invite.email,
        firstName: first,
        lastName: last,
      })
    }
    for (const access of userAccess) {
      rows.push({
        kind: 'access',
        access,
        key: `access:${access.uuid}`,
        name: displayName(access.user_name, access.user_email),
        email: access.user_email ?? '',
        firstName: '',
        lastName: '',
      })
    }

    // Anyone on the staff list who is not already shown as granted or invited.
    const shownEmails = new Set(
      rows.map((r) => (r.email ?? '').trim().toLowerCase()).filter(Boolean),
    )
    for (const member of partnerMembers) {
      const email = (member.user_email ?? '').trim().toLowerCase()
      if (!email || shownEmails.has(email)) continue
      shownEmails.add(email)
      rows.push({
        kind: 'member',
        userUuid: member.user_uuid,
        key: `member:${member.uuid}`,
        name: displayName(member.user_name, member.user_email),
        email: member.user_email ?? '',
        firstName: '',
        lastName: '',
      })
    }

    const rank = (r: PersonRow) =>
      r.kind === 'access' ? 2 : r.kind === 'invite' && r.invite.sent_at ? 1 : 0
    return rows.sort(
      (a, b) => rank(a) - rank(b) || (a.name ?? '').localeCompare(b.name ?? ''),
    )
  }, [
    showContactRow,
    partnership?.contact_email,
    partnership?.contact_first_name,
    partnership?.contact_last_name,
    pendingInvites,
    userAccess,
    partnerMembers,
    // displayName resolves its fallback through t(), so the list has to
    // recompute when the language changes.
    t,
  ])

  // Coordinator-editable contractor status/priority on the downstream org.
  // Only the changed keys are sent; the values flow back through a fresh
  // fetchPartnership so the card re-renders from the source of truth.
  const saveContractor = useCallback(
    async (values: Record<string, unknown>) => {
      if (!orgUuid || !uuid) return
      const payload: { contractor_status?: string; contractor_priority?: string } = {}
      if (typeof values.contractor_status === 'string') {
        payload.contractor_status = values.contractor_status
      }
      if (typeof values.contractor_priority === 'string') {
        payload.contractor_priority = values.contractor_priority
      }
      if (Object.keys(payload).length === 0) return
      setSavingContractor(true)
      try {
        await api.put(`/organisations/${orgUuid}/partnerships/${uuid}/`, payload)
        toast.success(
          t('partnerships.contractorUpdated', 'Contractor details updated.'),
        )
        await fetchPartnership()
      } catch (err) {
        toastApiError(
          err,
          t(
            'partnerships.failedToUpdateContractor',
            'Failed to update contractor details.',
          ),
        )
      } finally {
        setSavingContractor(false)
      }
    },
    [orgUuid, uuid, fetchPartnership, t],
  )

  // Invite form. Email is prefilled from the downstream org's registered
  // contact (carried over by migration); the admin can edit it - e.g. to
  // replace a placeholder - before sending. Posts to the org-membership
  // invite endpoint on the downstream org; acceptance creates the user +
  // membership, which activates the partnership.
  const sendInvite = useCallback(
    async (email: string, firstName: string, lastName: string) => {
      const downstreamUuid = partnership?.downstream_organisation.uuid
      const trimmed = (email ?? '').trim()
      if (!downstreamUuid || !trimmed) return
      setInviting(true)
      setInvitingEmail(trimmed.toLowerCase())
      try {
        await api.post(`/organisations/${downstreamUuid}/members/invite/`, {
          email: trimmed,
          first_name: (firstName ?? '').trim(),
          last_name: (lastName ?? '').trim(),
          role_uuid: partnership?.downstream_invite_role_uuid || undefined,
        })
        toast.success(t('partnerships.invitationSent', 'Invitation sent.'))
        setAddDialogOpen(false)
        // Only the invitation list changed. Refetching the partnership pulled
        // its 589-row warranty summary (283KB) again on every click, and
        // access cannot have changed - it is created when they accept.
        void refetchPendingInvites()
      } catch (err) {
        toastApiError(
          err,
          t('partnerships.failedToSendInvitation', 'Failed to send invitation.'),
        )
      } finally {
        setInviting(false)
        setInvitingEmail(null)
      }
    },
    [
      partnership?.downstream_organisation.uuid,
      partnership?.downstream_invite_role_uuid,
      refetchPendingInvites,
      t,
    ],
  )

  // An invitation that was never sent is not a mistake to undo, it is a person
  // waiting to be invited - so cancelling is only offered once one has gone out.
  const cancelInvite = useCallback(
    async (invite: PendingInvite) => {
      const downstreamUuid = partnership?.downstream_organisation.uuid
      if (!downstreamUuid) return
      setCancellingInvite(invite.uuid)
      try {
        await api.delete(
          `/organisations/${downstreamUuid}/invitations/${invite.uuid}/`,
        )
        toast.success(t('partnerships.invitationCancelled', 'Invitation cancelled.'))
        void refetchPendingInvites()
      } catch (err) {
        toastApiError(
          err,
          t('partnerships.failedToCancel', 'Failed to cancel invitation.'),
        )
      } finally {
        setCancellingInvite(null)
      }
    },
    [partnership?.downstream_organisation.uuid, refetchPendingInvites, t],
  )

  const resendInvite = useCallback(
    async (invite: PendingInvite) => {
      const downstreamUuid = partnership?.downstream_organisation.uuid
      if (!downstreamUuid) return
      setResendingInvite(invite.uuid)
      try {
        await api.post(
          `/organisations/${downstreamUuid}/invitations/${invite.uuid}/resend/`,
        )
        toast.success(t('partnerships.invitationResent', 'Invitation resent.'))
        void refetchPendingInvites()
      } catch (err) {
        toastApiError(err, t('partnerships.failedToResend', 'Failed to resend invitation.'))
      } finally {
        setResendingInvite(null)
      }
    },
    [partnership?.downstream_organisation.uuid, refetchPendingInvites, t],
  )

  const inviteFields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'first_name',
        label: 'First Name',
        placeholder: 'First name',
        value: partnership?.contact_first_name ?? '',
        autoFocus: true,
      },
      {
        type: 'text',
        name: 'last_name',
        label: 'Last Name',
        placeholder: 'Last name',
        value: partnership?.contact_last_name ?? '',
      },
      {
        type: 'email',
        name: 'email',
        label: 'Email',
        placeholder: 'colleague@example.com',
        value: partnership?.contact_email ?? '',
        className: 'col-span-2',
        required: 'Enter a valid email',
      },
    ],
    [
      partnership?.contact_email,
      partnership?.contact_first_name,
      partnership?.contact_last_name,
    ],
  )
  const contactFields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'first_name',
        label: 'First Name',
        placeholder: 'First name',
        value: partnership?.contact_first_name ?? '',
        autoFocus: true,
      },
      {
        type: 'text',
        name: 'last_name',
        label: 'Last Name',
        placeholder: 'Last name',
        value: partnership?.contact_last_name ?? '',
      },
      {
        type: 'email',
        name: 'email',
        label: 'Email',
        placeholder: 'colleague@example.com',
        value: partnership?.contact_email ?? '',
        className: 'col-span-2',
        // Deliberately NOT required: submitting it empty is how a contact is
        // cleared, which is a supported outcome rather than an incomplete form.
      },
    ],
    [
      partnership?.contact_email,
      partnership?.contact_first_name,
      partnership?.contact_last_name,
    ],
  )
  const { Form: ContactForm } = useForm(
    {
      name: 'partner-contact-form',
      submit: 'footer',
      fields: contactFields,
    },
    async (rawValues) => {
      const values = rawValues as Record<string, string>
      await saveContact({
        first_name: String(values.first_name ?? ''),
        last_name: String(values.last_name ?? ''),
        email: String(values.email ?? ''),
      })
    },
    [saveContact, contactFields],
  )

  const { Form: InviteForm, ref: inviteFormRef } = useForm(
    {
      name: 'partner-invite-form',
      submit: 'footer',
      fields: inviteFields,
    },
    async (rawValues) => {
      const values = rawValues as Record<string, string>
      await sendInvite(
        String(values.email ?? ''),
        String(values.first_name ?? ''),
        String(values.last_name ?? ''),
      )
    },
    [sendInvite, inviteFields],
  )

  // Contractor status/priority editor. Options come from the payload's
  // `contractor_choices` (never hardcoded); current values live on the
  // downstream org. Coordinator-editable, mirroring the suspend/edit
  // controls' `!isPartnerAccess` gate.
  const contractorChoices = partnership?.contractor_choices
  const contractorItems = useMemo<CardFieldItem[]>(() => {
    if (!partnership || !contractorChoices) return []
    return [
      {
        field: defineField({
          code_name: 'contractor_status',
          display_name: 'Status',
          field_type: 'SELECT',
          options: contractorChoices.statuses,
        }),
        value: partnership.downstream_organisation.contractor_status ?? '',
      },
      {
        field: defineField({
          code_name: 'contractor_priority',
          display_name: 'Priority',
          field_type: 'SELECT',
          options: contractorChoices.priorities,
        }),
        value: partnership.downstream_organisation.contractor_priority ?? '',
      },
    ]
  }, [partnership, contractorChoices])

  const contractorEditConfig = useMemo<EditConfig>(
    () => ({
      onSave: saveContractor,
      isPending: savingContractor,
      canEdit: !isPartnerAccess,
    }),
    [saveContractor, savingContractor, isPartnerAccess],
  )

  useEffect(() => {
    // Clear before refetching. Both callbacks change identity exactly when
    // orgUuid/uuid change, so this is the navigation boundary: without the
    // reset the previous partner's name, users and invitations stay painted
    // until the new fetch lands, which looks like the click was ignored.
    setLoading(true)
    setPartnership(null)
    setUserAccess([])
    fetchPartnership()
    fetchUserAccess()
  }, [fetchPartnership, fetchUserAccess])

  async function toggleActive() {
    if (!partnership) return
    setTogglingStatus(true)
    const action = partnership.is_active ? 'suspend' : 'reactivate'
    try {
      await api.post(`/organisations/${orgUuid}/partnerships/${uuid}/${action}/`)
      toast.success(partnership.is_active ? 'Partner access suspended.' : 'Partner access restored.')
      fetchPartnership()
    } catch (err) {
      toastApiError(err, `Failed to ${action} partner.`);
    } finally {
      setTogglingStatus(false)
    }
  }


  // A staff member on the partner's list without a grant cannot open the
  // portal for this partner; give it here rather than via a new invitation.
  async function giveAccess(userUuid: string) {
    setGrantingUuid(userUuid)
    try {
      await api.post(`/organisations/${orgUuid}/partnerships/${uuid}/user-access/`, {
        user_uuid: userUuid,
      })
      toast.success(t('partnerships.accessGiven', 'Access given.'))
      fetchUserAccess()
      fetchPartnership()
    } catch (err) {
      toastApiError(err, t('partnerships.failedToGiveAccess', 'Failed to give access.'))
    } finally {
      setGrantingUuid(null)
    }
  }

  async function revokeAccess(accessUuid: string) {
    setRevokingUuid(accessUuid)
    try {
      await api.delete(`/organisations/${orgUuid}/partnerships/${uuid}/user-access/${accessUuid}/`)
      toast.success(t('partnerships.accessRemoved', 'User access removed.'))
      setConfirmRevoke(null)
      fetchUserAccess()
      fetchPartnership()
    } catch (err) {
      toastApiError(
        err,
        t('partnerships.failedToRemoveAccess', 'Failed to remove user access.'),
      );
    } finally {
      setRevokingUuid(null)
    }
  }

  return (
    <AppShell>
      <main className="page-container py-10">
        {/* Back */}
        <Button
          variant="ghost"
          onClick={() => navigate(backPath)}
          className="mb-6 flex cursor-pointer items-center gap-1.5 border-none bg-transparent text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
        >
          <ArrowLeft className="h-3 w-3" />
          {linkedFrom?.backLabel ??
            t('partnerships.backToNetwork', 'Back to Network')}
        </Button>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
          </div>
        ) : !partnership ? (
          <p className="clara-small text-center py-12">Partner not found.</p>
        ) : (
          <>
            {/* Parent breadcrumb - shown only when this partnership is a
                sub-location of a parent partnership. Clicking navigates
                back up to the parent's detail page. */}
            {partnership.parent_partnership && (
              <button
                onClick={() =>
                  navigate(
                    `/network/${partnership.parent_partnership!.uuid}`,
                  )
                }
                className="mb-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)] hover:text-[var(--green)] cursor-pointer"
              >
                <ArrowUpRight className="h-3 w-3 rotate-[225deg]" />
                Part of{' '}
                <span className="underline">
                  {partnership.parent_partnership.downstream_organisation.name}
                </span>
              </button>
            )}

            {/* Header */}
            <div className="flex items-start justify-between mb-8">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="h-4 w-4 text-[var(--ink-3)]" />
                  <h1 className="clara-title">{partnership.downstream_organisation.name}</h1>
                </div>
                <p className="text-[13px] text-[var(--ink-3)]">
                  Partner details and user management
                </p>
              </div>
              {/* Suspend/Reactivate - subtle Clara Vale style. Hidden from
                  partner (dealer) context: a dealer cannot suspend or
                  reactivate their own partnership (the endpoint also 403s
                  them). */}
              <div className="flex items-center gap-2">
              {/* One menu, not three competing verbs. A button has room for a
                  word; these actions need a sentence - "Merge" does not say
                  which direction it runs, "Regroup" is our word not the
                  industry's, and "bring someone here" actually MOVES a person
                  off another partner. The row description is where that gets
                  said, once, at the moment of use. */}
              {!isPartnerAccess && (
                <Popover open={manageOpen} onOpenChange={setManageOpen}>
                  <PopoverTrigger
                    data-testid="partner-manage"
                    className="flex cursor-pointer items-center gap-1.5 rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-transparent px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-2)] transition-all duration-150"
                  >
                    {t('partnerships.manage', 'Manage')}
                    <ChevronDown className="h-3 w-3" />
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-[340px] p-1.5">
                    {can('partnerships.restructure') && (
                      <>
                        <MenuAction
                          testId="merge-partners"
                          title={t(
                            'partnerships.mergeAction',
                            'Merge duplicates into this',
                          )}
                          description={t(
                            'partnerships.mergeActionHint',
                            'Combine other records into this one. The others are retired.',
                          )}
                          onSelect={() => {
                            setManageOpen(false)
                            navigate(`/network/${partnership.uuid}/merge`)
                          }}
                        />
                        <MenuAction
                          testId="regroup-partner"
                          title={t(
                            'partnerships.regroupAction',
                            'Move under another partner',
                          )}
                          description={t(
                            'partnerships.regroupActionHint',
                            'Change which group this record sits under.',
                          )}
                          onSelect={() => {
                            setManageOpen(false)
                            setRegroupOpen(true)
                          }}
                        />
                      </>
                    )}
                    {can('partnerships.restructure') && (
                      <MenuAction
                        testId="bring-someone-here"
                        tone="warn"
                        title={t(
                          'partnerships.accessAction',
                          'Move someone here',
                        )}
                        description={t(
                          'partnerships.accessActionHint',
                          'Take a person from another partner. They lose access there.',
                        )}
                        onSelect={() => {
                          setManageOpen(false)
                          setAccessOpen(true)
                        }}
                      />
                    )}
                    <MenuAction
                      testId="partner-suspend"
                      tone={partnership.is_active ? 'warn' : 'go'}
                      disabled={togglingStatus}
                      title={
                        partnership.is_active
                          ? t('partnerships.suspendAccess', 'Suspend access')
                          : t('partnerships.restoreAccess', 'Restore access')
                      }
                      description={
                        partnership.is_active
                          ? t(
                              'partnerships.suspendAccessHint',
                              'Nobody at this partner can sign in until restored.',
                            )
                          : t(
                              'partnerships.restoreAccessHint',
                              'Let this partner sign in again.',
                            )
                      }
                      onSelect={() => {
                        setManageOpen(false)
                        toggleActive()
                      }}
                    />
                  </PopoverContent>
                </Popover>
              )}
              </div>
            </div>

            {/* The queue already knows this record has twins. Without saying so
                here, an operator has to find Merge and retype the partner's own
                name to rediscover what the scan computed - and can silently
                miss one. */}
            {!isPartnerAccess &&
              can('partnerships.restructure') &&
              (duplicateGroup?.members?.length ?? 0) > 1 && (
                <div
                  data-testid="duplicate-banner"
                  className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-5 py-4"
                >
                  <p className="text-[13px] text-[var(--ink-2)]">
                    {/* Plural lives in the seeded catalogue (_one/_other); an
                        inline default cannot pluralise, which is how this first
                        rendered "1 possible duplicates". */}
                    {t('partnerships.duplicatesFound', {
                      count: (duplicateGroup?.members?.length ?? 1) - 1,
                      defaultValue_one:
                        '1 possible duplicate of this record was found.',
                      defaultValue_other:
                        '{{count}} possible duplicates of this record were found.',
                    })}
                  </p>
                  <Button
                    variant="outline"
                    size="xs"
                    data-testid="review-duplicates"
                    onClick={() =>
                      navigate(
                        `/network/${partnership.uuid}/merge?preselect=${(duplicateGroup?.members ?? [])
                          .map((m) => m.partnership_uuid)
                          .filter((u) => u && u !== partnership.uuid)
                          .join(',')}`,
                      )
                    }
                  >
                    {t('partnerships.reviewDuplicates', 'Review duplicates')}
                  </Button>
                </div>
              )}

            {/* Info cards */}
            <div
              className="mb-10 grid grid-cols-2 gap-6 rounded-[8px] border-[0.5px] border-[var(--rule)] p-5 md:grid-cols-4"
            >
              <div>
                <p className="clara-label mb-1">Role</p>
                <p className="text-[13px] text-[var(--ink)]">
                  {partnership.role_name}
                </p>
              </div>
              <div>
                <p className="clara-label mb-1">User Management</p>
                <p className="text-[13px] text-[var(--ink)]">
                  {STRATEGY_LABELS[partnership.user_management_strategy] ?? partnership.user_management_strategy}
                </p>
              </div>
              <div>
                <p className="clara-label mb-1">Two-Factor Auth</p>
                <span
                  className={`rounded-[3px] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] ${
                    partnership.downstream_mfa_required
                      ? 'bg-[var(--green-bg)] text-[var(--green)]'
                      : 'bg-[var(--bg-subtle)] text-[var(--ink-3)]'
                  }`}
                >
                  {partnership.downstream_mfa_required ? 'Required' : 'Not required'}
                </span>
              </div>
              <div>
                <p className="clara-label mb-1">Status</p>
                <span
                  className={`rounded-[3px] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] ${
                    partnership.is_active
                      ? 'bg-[var(--status-ok-bg)] text-[var(--status-ok)]'
                      : 'bg-[var(--status-error-bg)] text-[var(--status-error)]'
                  }`}
                >
                  {partnership.is_active ? 'Active' : 'Suspended'}
                </span>
              </div>
            </div>

            {/* Contractor status + priority - coordinator-editable. Hidden
                until the payload carries the choice lists (i.e. only on
                tenants that expose the contractor editor). Notes are handled
                by the dedicated NotesPanel below, not here. */}
            {contractorItems.length > 0 && (
              <EditableDetailGridCard
                title={t('partnerships.contractorCard', 'Contractor')}
                icon={<HardHat className="h-3.5 w-3.5" />}
                items={contractorItems}
                editConfig={contractorEditConfig}
                className="mb-10"
              />
            )}

            {/* Users section */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h3 className="clara-section-label">
                  {t('partnerships.peopleAtPartner', 'People')}
                </h3>
                <div className="flex items-center gap-2">
                {/* Moving an existing person across lives in Manage, with the
                    other rare, consequential actions - it is the one whose old
                    name ("bring someone here") hid the fact that it REMOVES
                    them from the partner they are on today. */}
                <Button
                  onClick={() => {
                    // Blank, not the registered contact: this invites a NEW
                    // colleague. Prefilling the contact email here made every
                    // invite default to the person who is already onboarded.
                    // The migrated-contact row below has its own Invite that
                    // does prefill, because it onboards that exact contact.
                    inviteFormRef.current?.resetForm({
                      values: { first_name: '', last_name: '', email: '' },
                    })
                    setAddDialogOpen(true)
                  }}
                  className="px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] rounded-[4px] transition-all duration-150 cursor-pointer flex items-center gap-1.5 bg-[var(--green)] text-[var(--bg)] border-none hover:bg-[var(--green-hover)]"
                >
                  <Plus className="h-3 w-3" />
                  {t('partnerships.inviteUser', 'Invite User')}
                </Button>
                </div>
              </div>

              {/* ONE list, ordered by where each person is in the invitation
                  journey: still to invite, invited and waiting, then signed in.
                  These used to be three separate blocks, each with its own
                  Name/Email header, so an accepted colleague could sit between
                  two people nobody had contacted yet - and after a merge a
                  dealership carries several of each. */}
              {people.length === 0 ? (
                <div className="max-w-[480px] rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5">
                  <p className="font-display text-[15px] text-[var(--ink-3)]">
                    {t(
                      'partnerships.noUsers',
                      'No users have been added to this partner yet',
                    )}
                  </p>
                </div>
              ) : (
                <div>
                  <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_210px] gap-4 border-b-[0.5px] border-b-[var(--rule-strong)] pb-3">
                    <span className="clara-th">Name</span>
                    <span className="clara-th">Email</span>
                    <span className="clara-th w-24" />
                  </div>
                  {people.map((person) => (
                    <div
                      key={person.key}
                      /* Fixed last column, not auto: an auto column is sized by
                         each row's own content, so the wide "Invite" button
                         narrowed the columns beside it and that row's email sat
                         left of every other one. Wide enough for the widest
                         actions (Invite + Change, No access + Give access). */
                      className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_210px] items-center gap-4 border-b-[0.5px] border-b-[var(--rule)] py-3"
                    >
                      <div className="flex items-center gap-2">
                        <Users className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                        <span className="text-[13px] text-[var(--ink)]">
                          {person.name}
                        </span>
                      </div>
                      <p className="break-all text-[13px] text-[var(--ink-2)]">
                        {person.email}
                      </p>

                      {person.kind === 'access' ? (
                        <div className="flex items-center justify-end">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => setConfirmRevoke(person.access)}
                            disabled={revokingUuid === person.access.uuid}
                            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-[4px] border-none text-[var(--ink-3)] transition-all duration-150 hover:bg-[var(--bg-subtle)] disabled:opacity-50"
                            title={t('partnerships.removeAccess', 'Remove access')}
                          >
                            {revokingUuid === person.access.uuid ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                      ) : person.kind === 'member' ? (
                        <div className="flex items-center justify-end gap-2">
                          <span className="whitespace-nowrap rounded-[3px] bg-[var(--status-error-bg)] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[var(--status-error)]">
                            {t('partnerships.noAccess', 'No access')}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid="partner-give-access"
                            onClick={() => void giveAccess(person.userUuid)}
                            disabled={grantingUuid === person.userUuid}
                            className="cursor-pointer whitespace-nowrap rounded-[4px] px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] disabled:opacity-50"
                          >
                            {grantingUuid === person.userUuid ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              t('partnerships.giveAccess', 'Give access')
                            )}
                          </Button>
                        </div>
                      ) : person.kind === 'invite' && person.invite.sent_at ? (
                        <div className="flex items-center justify-end gap-2">
                          <span className="rounded-[3px] bg-[var(--bg-subtle)] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                            {t('partnerships.invited', 'Invited')}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void resendInvite(person.invite)}
                            disabled={resendingInvite === person.invite.uuid}
                            className="cursor-pointer rounded-[4px] px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] disabled:opacity-50"
                          >
                            {resendingInvite === person.invite.uuid ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              t('partnerships.resend', 'Resend')
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void cancelInvite(person.invite)}
                            disabled={cancellingInvite === person.invite.uuid}
                            className="cursor-pointer rounded-[4px] px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] disabled:opacity-50"
                          >
                            {cancellingInvite === person.invite.uuid ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              t('common.cancel', 'Cancel')
                            )}
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              void sendInvite(
                                person.email,
                                person.firstName,
                                person.lastName,
                              )
                            }
                            disabled={invitingEmail === person.email.toLowerCase()}
                            className="flex cursor-pointer items-center gap-1.5 rounded-[4px] px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] disabled:opacity-50"
                          >
                            {invitingEmail === person.email.toLowerCase() ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Mail className="h-3 w-3" />
                            )}
                            {t('partnerships.sendInvite', 'Invite')}
                          </Button>
                          {/* The contact row is synthesised from the downstream
                              org's stored contact details, not from a
                              membership, access row or invitation - so there
                              was no row to remove and no way to correct it.
                              When the named person moves to another site this
                              is the only thing still pointing at them. */}
                          {person.kind === 'contact' && canEditPartnerships && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingContact(true)}
                              className="cursor-pointer rounded-[4px] px-3 py-1.5 text-[11px] uppercase tracking-[0.1em]"
                            >
                              {t('partnerships.changeContact', 'Change')}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

            </section>

            {/* Tenant-defined partner profile (EAV) - tier, membership #, etc.
                `key={partnership.uuid}` ties the form instance to the
                *fetched* partnership, not the URL param. Navigating
                parent → child updates the URL immediately but the
                fetched partnership (and its attribute_values) only
                arrives after the API roundtrip; keying on the fetched
                uuid ensures the form remounts exactly when fresh
                values are available, so the draft never shows stale
                parent values.

                ASM (Area Sales Manager) attributes are the tenant's
                point of contact for this partner, not fields the
                partner themselves owns - they're only meaningful on
                the HQ row, so we hide them from child locations and
                render them in their own card below to make the
                distinction obvious in the UI. */}
            {/* Contractor-oriented partner profile (tier, OC Connect
                membership, ASM, etc.) is not relevant to HOA / Developer
                partners — their content is the Communities & Units section
                below. Role-scoped partner attributes are a follow-up; for now
                we suppress the contractor profile for this role. */}
            {partnership.role_name !== 'HOA / Developer' &&
              partnership.attribute_values &&
              partnership.attribute_values.length > 0 && (
                <>
                  <PartnershipAttributeForm
                    key={`${partnership.uuid}-profile`}
                    partnershipUuid={uuid!}
                    attributeValues={partnership.attribute_values}
                    excludeCodes={PROFILE_FORM_EXCLUDED_CODES}
                    onSaved={fetchPartnership}
                  />
                  {!partnership.parent_partnership && (
                    <PartnershipAttributeForm
                      key={`${partnership.uuid}-asm`}
                      partnershipUuid={uuid!}
                      attributeValues={partnership.attribute_values}
                      includeCodes={ASM_ATTRIBUTE_CODES}
                      title={t('partnerships.areaSalesManager', 'Area Sales Manager')}
                      onSaved={fetchPartnership}
                    />
                  )}
                </>
              )}

            {/* Sub-locations. Rendered only when this partnership has
                children (e.g. a multi-location contractor). Each row is
                a clickable card that navigates to that location's own
                detail page. */}
            {partnership.child_partnerships &&
              partnership.child_partnerships.length > 0 && (
                <section className="mt-8 border-t-[0.5px] border-t-[var(--rule)] pt-8">
                  <div className="mb-4 flex items-baseline justify-between">
                    <h3 className="clara-section-label">
                      Locations ({partnership.child_partnerships.length})
                    </h3>
                  </div>
                  <div className="flex flex-col divide-y divide-[var(--rule-light)] border-[0.5px] border-[var(--rule)] rounded-[8px] overflow-hidden">
                    {partnership.child_partnerships.map((child) => {
                      const secondary = (
                        ['location_name', 'city', 'state', 'membership_number'] as const
                      )
                        .map((code) => child.attribute_summary?.[code])
                        .filter(
                          (v): v is string =>
                            typeof v === 'string' && v.trim().length > 0,
                        )
                      const tier =
                        typeof child.attribute_summary?.tier === 'string'
                          ? (child.attribute_summary.tier as string)
                          : null
                      return (
                        <button
                          key={child.uuid}
                          onClick={() => navigate(`/network/${child.uuid}`)}
                          className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer transition-colors duration-150 hover:bg-[var(--bg-subtle)] text-left"
                        >
                          <div className="flex items-start gap-2 min-w-0 flex-1">
                            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                            <div className="flex min-w-0 flex-1 flex-col">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="truncate text-[13px] text-[var(--ink)]">
                                  {child.downstream_organisation.name}
                                </span>
                                {child.is_active === false && (
                                  <span className="shrink-0 rounded-full bg-[var(--status-error-bg)] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[var(--status-error)]">
                                    Suspended
                                  </span>
                                )}
                              </div>
                              {secondary.length > 0 && (
                                <span className="truncate text-[11px] text-[var(--ink-3)]">
                                  {secondary.join(' · ')}
                                </span>
                              )}
                            </div>
                          </div>
                          {tier && (
                            <span className="ml-auto shrink-0 rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[9px] font-[500] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                              {tier}
                            </span>
                          )}
                          <ChevronRight className="h-4 w-4 shrink-0 text-[var(--ink-3)]" />
                        </button>
                      )
                    })}
                  </div>
                </section>
              )}

            {/* Registered warranties - a live list of what the
                contractor has actually sold, grouped by warranty class
                with a drill-down to the specific registrations. */}
            <div className="mt-8 border-t-[0.5px] border-t-[var(--rule)] pt-8">
              <PartnershipWarrantyRegistrations partnershipUuid={uuid!} />
            </div>

            {/* Product access */}
            <div className="mt-8 border-t-[0.5px] border-t-[var(--rule)] pt-8">
              <PartnershipProductAccess partnershipUuid={uuid!} />
            </div>

            {/* Performance + Notes - side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              {/* Performance */}
              <div
                className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-6"
              >
                <p className="mb-4 text-[10px] font-[400] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  Performance
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="font-display text-[28px] leading-none text-[var(--ink)]">
                      {partnership.total_user_access_count}
                    </p>
                    <p className="mt-1 text-[10px] font-[400] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                      Users
                    </p>
                  </div>
                  <div>
                    <p className="font-display text-[28px] leading-none text-[var(--ink)]">
                      0
                    </p>
                    <p className="mt-1 text-[10px] font-[400] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                      Registrations
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <NotesPanel
                recordId={partnership.downstream_organisation.uuid ?? uuid ?? ''}
                recordType="registration"
                canAddInternal={true}
                canAddCustomerVisible={false}
              />
            </div>
          </>
        )}

        {/* Invite dialog - generated form, email prefilled from the
            downstream org's registered contact (editable). */}
        <Dialog
          open={addDialogOpen}
          onOpenChange={(open) => {
            setAddDialogOpen(open)
            if (!open) {
              inviteFormRef.current?.resetForm({
                values: {
                  first_name: '',
                  last_name: '',
                  email: partnership?.contact_email ?? '',
                },
              })
            }
          }}
        >
          <DialogContent className="w-full max-w-md">
            <DialogHeader className="mb-5">
              <DialogTitle className="text-[15px] text-[var(--ink)]">
                Invite a user to {partnership?.downstream_organisation.name}
              </DialogTitle>
              <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
                They will receive an email invitation to join.
              </p>
            </DialogHeader>

            <InviteForm className="grid grid-cols-2 gap-3" />
            <DialogFooter className="flex justify-end gap-3 border-t-[0.5px] border-t-[var(--rule)] pt-3">
              <Button
                variant="outline"
                type="button"
                onClick={() => setAddDialogOpen(false)}
                className="cursor-pointer border-none bg-transparent px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="partner-invite-form"
                disabled={inviting}
                className="flex cursor-pointer items-center gap-1.5 rounded-[4px] border-none bg-[var(--green)] px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 disabled:opacity-50"
              >
                {inviting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                Send Invitation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={editingContact}
          onOpenChange={(open) => {
            if (!open) setEditingContact(false)
          }}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {t('partnerships.contactTitle', 'Registered contact')}
              </DialogTitle>
              <DialogDescription>
                {t(
                  'partnerships.contactDescription',
                  'Who to contact at this partner. Reassign it when the named person moves on, or clear it if nobody has taken over yet - it is not used to send anything.',
                )}
              </DialogDescription>
            </DialogHeader>
            <ContactForm className="grid grid-cols-2 gap-3" />
            <DialogFooter className="flex justify-between gap-3 border-t-[0.5px] border-t-[var(--rule)] pt-3">
              {/* Clearing is a first-class outcome, not a hidden one: a dealer
                  with no current contact is honestly contactless, and the
                  alternative is a made-up name on a real partner. */}
              <Button
                variant="ghost"
                type="button"
                disabled={savingContact}
                onClick={() =>
                  void saveContact({ first_name: '', last_name: '', email: '' })
                }
                className="cursor-pointer px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--status-error)] disabled:opacity-50"
              >
                {t('partnerships.clearContact', 'Clear contact')}
              </Button>
              <span className="flex gap-3">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setEditingContact(false)}
                  className="cursor-pointer border-none bg-transparent px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  form="partner-contact-form"
                  disabled={savingContact}
                  className="flex cursor-pointer items-center gap-1.5 rounded-[4px] border-none bg-[var(--green)] px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] disabled:opacity-50"
                >
                  {savingContact && <Loader2 className="h-3 w-3 animate-spin" />}
                  {t('partnerships.saveContact', 'Save contact')}
                </Button>
              </span>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmDeleteDialog
          open={confirmRevoke !== null}
          onOpenChange={(open) => {
            if (!open) setConfirmRevoke(null)
          }}
          title={t('partnerships.removeAccessTitle', 'Remove user access?')}
          description={
            confirmRevoke
              ? `${confirmRevoke.user_name || confirmRevoke.user_email} will lose access to this partnership.`
              : undefined
          }
          confirmLabel="Remove access"
          isPending={revokingUuid === confirmRevoke?.uuid}
          onConfirm={() => {
            if (confirmRevoke) void revokeAccess(confirmRevoke.uuid)
          }}
        />

        {/* Guarded: the not-found / loading branches above are a ternary
            INSIDE the JSX, so anything after it still renders while
            `partnership` is null. */}
        {partnership && (
          <MoveAccessDialog
            open={accessOpen}
            onOpenChange={setAccessOpen}
            targetPartnershipUuid={partnership.uuid}
            targetPartnerName={partnership.downstream_organisation.name}
            targetParentUuid={
              partnership.downstream_organisation.parent_organisation_uuid ?? null
            }
            targetParentName={
              partnership.downstream_organisation.parent_organisation_name ?? null
            }
            onChanged={() => {
              void fetchPartnership()
              void fetchUserAccess()
            }}
          />
        )}

        {partnership && (
          <RegroupPartnerDialog
            open={regroupOpen}
            onOpenChange={setRegroupOpen}
            partnershipUuid={partnership.uuid}
            partnerName={partnership.downstream_organisation.name}
            currentParentName={
              partnership.parent_partnership?.downstream_organisation?.name ?? null
            }
            onRegrouped={() => void fetchPartnership()}
          />
        )}
      </main>
    </AppShell>
  );
}
